import { createStore } from '@oneappexchange/lwc-wired';
import { Draft } from 'immer';
import { IS_DEVELOPMENT } from '~/modules/config';
import { getUniqueIteratorKey } from '~/modules/utilities/lwcUtilities';
import { logger } from '../utilities/logger';

export enum NotificationType {
    Error = 'error',
    Success = 'success',
}

export interface Notification {
    iteratorKey: string;
    type: NotificationType;
    message: string;
    previousFocusedElement: HTMLElement | null;
    title: string | null;
}

type NotificationState = {
    notifications: Notification[];
    id: number;
};

const initialState: NotificationState = {
    notifications: [],
    id: 0,
};

export const notificationsStore = createStore(
    initialState,
    (set: SetState<NotificationState>, get: GetState<NotificationState>) => ({
        reset() {
            set(function reset() {
                return initialState;
            });
        },
        addNotification(
            message: string,
            type: NotificationType,
            options: { previousFocusedElement: Notification['previousFocusedElement']; title: string } = {
                previousFocusedElement: null,
                title: '',
            },
        ) {
            const { previousFocusedElement, title } = options;
            set(function addNotification(draft) {
                if (
                    draft.notifications.length === 0 ||
                    !notificationAlreadyExists(message, type, draft.notifications)
                ) {
                    draft.id++;
                    draft.notifications.push({
                        iteratorKey: getUniqueIteratorKey(),
                        message: message,
                        type: type,
                        title: title,
                        previousFocusedElement: previousFocusedElement as Draft<HTMLElement>,
                    });
                } else {
                    logger.warn(`Attempted to insert duplicate notification: message=${message}, type=${type},`);
                }
            });
            return get().id;
        },
        removeNotification(iteratorKey: string) {
            set(function removeNotification(draft) {
                for (let i = 0; i < draft.notifications.length; i++) {
                    if (draft.notifications[i].iteratorKey === iteratorKey) {
                        draft.notifications.splice(i, 1);
                        return;
                    }
                }
            });
        },
    }),
    'notificationStore',
    IS_DEVELOPMENT,
);

export function selectNotifications(state: NotificationState) {
    return state.notifications;
}

function notificationAlreadyExists(newMessage: string, newType: NotificationType, notifications: Notification[]) {
    return notifications.some(({ message, type }) => message === newMessage && type === newType);
}
